import { useMemo, useContext, useState } from 'react'
import { Button, Dropdown, Empty, Flex, Progress, Space, Table, Tooltip, Typography } from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  MoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import { ProgresSpinner } from '../../common/ProgresSpinner'
import { downloadFile, formatDateTime, formatEmptyValue } from '../../../utils/common'
import { useIntl } from 'react-intl'
import { NavContext } from '../../../context/global_context'
import { notifyError } from '../../../utils/notifications'
import { RESULT_FILE_CONFIG } from './constants'
import IconXmlFile from '../../icons/IconXmlFile'

const CapitalHarnessTable = (props) => {
  const {
    dataSource,
    pagination,
    loading,
    expandedRowKeys,
    expandedDetails,
    selectedIds,
    inProcessGenerateFileIds,
    onToggleExpand,
    onRunFileGeneration,
    onClone,
    onDelete,
    onSelect,
    onDownloadLogs,
  } = props
  const intl = useIntl()
  const [, navDispatch] = useContext(NavContext)
  // Source files run to hundreds of MB: {harness id: percent, or null when the size is unknown}
  const [sourceDownloads, setSourceDownloads] = useState({})

  const downloadSourceFile = async (record) => {
    setSourceDownloads((prev) => ({ ...prev, [record.id]: 0 }))
    const saved = await downloadFile(
      `${record.capital_harness_file}?timestamp=${Number(new Date())}`,
      `${record.name}.xml`,
      (percent) => setSourceDownloads((prev) => ({ ...prev, [record.id]: percent }))
    )
    setSourceDownloads(({ [record.id]: _done, ...rest }) => rest)
    if (!saved) {
      notifyError(navDispatch, intl.formatMessage({ id: 'capitalHarness.downloadError' }))
    }
  }

  const columns = useMemo(() => [
    {
      title: intl.formatMessage({ id: 'generic.name' }),
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      ellipsis: { showTitle: false },
      render: (text) => <EllipsisTooltip className="ellipsis">{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'capitalHarness.abbreviation' }),
      dataIndex: 'abbreviation',
      sorter: (a, b) => (a.abbreviation || '').localeCompare(b.abbreviation || ''),
      ellipsis: { showTitle: false },
      render: (text) => <EllipsisTooltip className="ellipsis">{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'generic.date' }),
      dataIndex: 'updated_at',
      sorter: (a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''),
      defaultSortOrder: 'descend',
      render: (text) => <EllipsisTooltip className="ellipsis">{formatDateTime(text)}</EllipsisTooltip>,
    },
    {
      title: '',
      align: 'right',
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.id)
        const isGenerating = (inProcessGenerateFileIds || []).includes(record.id)
        return (
          <Space size="small">
            <Tooltip title={ intl.formatMessage({ id: isExpanded ? 'capitalHarness.hideFiles' : 'capitalHarness.viewFiles' }) }>
              <div>
                <Button
                  type="link"
                  icon={isExpanded ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  className={`${isExpanded ? 'link-button neutral' : 'link-button'}`}
                  onClick={() => onToggleExpand(record)}
                  data-testid="capital-harness-files-toggle"
                >
                  {intl.formatMessage({ id: 'forms.files' })}
                </Button>
              </div>
            </Tooltip>

            {isGenerating ? (
              <ProgresSpinner
                caption={intl.formatMessage({ id: 'capitalHarness.fileGenerationInProgress' })}
                className="upload-inprogress"
                size="medium"
              />
            ) : (
              <>
                {record?.log?.error?.length > 0 && (
                  <Tooltip title={ intl.formatMessage({ id: 'capitalHarness.downloadErrorLogs' }) }>
                    <div>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        className="link-button"
                        onClick={() => onDownloadLogs(record)}
                      >
                        {intl.formatMessage({ id: 'salesCodeComplexity.errorLogs' })}
                      </Button>
                    </div>
                  </Tooltip>
                )}

                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  overlayStyle={{ marginTop: 12 }}
                  menu={{
                    items: [
                      {
                        key: 'run_file_generation',
                        label: intl.formatMessage({ id: 'capitalHarness.runFileGeneration' }),
                        icon: <ThunderboltOutlined />,
                      },
                      {
                        key: 'copy_circuit_details_from',
                        label: intl.formatMessage({ id: 'capitalHarness.copyCircuitDetailsFrom' }),
                      },
                      {
                        key: 'copy_devices_and_connector_attributes_from',
                        label: intl.formatMessage({ id: 'capitalHarness.copyDevicesAndConnectorAttributesFrom' }),
                      },
                      {
                        key: 'delete',
                        label: intl.formatMessage({ id: 'forms.delete' }),
                        icon: <DeleteOutlined style={{ color: 'var(--color-form-link-danger)' }} />,
                        danger: true,
                      },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'run_file_generation') onRunFileGeneration(record)
                      else if (key === 'delete') onDelete(record)
                      else onClone(record, key)
                    },
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </>
            )}
          </Space>
        )
      },
    },
  ], [expandedRowKeys, inProcessGenerateFileIds, onToggleExpand, onRunFileGeneration, onClone, onDelete, onDownloadLogs, intl])

  const rowSelection = {
    selectedRowKeys: selectedIds,
    hideSelectAll: true,
    onSelect: (record, selected) => onSelect(record),
  }

  return (
    <Table
      rowKey="key"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      showSorterTooltip={false}
      rowSelection={rowSelection}
      tableLayout='auto'
      scroll={dataSource?.length ? {x: 1000} : undefined}
      expandable={{
        expandIcon: () => null,
        showExpandColumn: false,
        expandedRowKeys,
        expandedRowRender: (record) => {
          const detail = expandedDetails[record.id] || record
          const isGenerating = (inProcessGenerateFileIds || []).includes(record.id)
          return (
            <div className="capital-harness-files-panel">
              <div className="files-grid">
                {RESULT_FILE_CONFIG.map((row, index) => {
                  const result = (detail?.results || []).find(
                    (item) => Number(item.file_type) === row.fileType
                  )
                  return (
                    <div key={`capital-harness-file-${index}`} className="file-item">
                      <div className="files-cell file-name">
                        <Flex align="center" gap={8}>
                          <FileExcelOutlined />
                          <Typography.Text>{row.name}</Typography.Text>
                        </Flex>
                      </div>
                      <div className="files-cell file-status">
                        {isGenerating ? (
                          <Typography.Text className="status-msg generation-in-progress">
                            <LoadingOutlined />
                            {intl.formatMessage({ id: 'capitalHarness.fileGenerationInProgress' })}
                          </Typography.Text>
                        ) : result?.result_file ? (
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            className="link-button"
                            onClick={async () => {
                              const saved = await downloadFile(
                                `${result.result_file}?timestamp=${Number(new Date())}`,
                                row.name
                              )
                              if (!saved) {
                                notifyError(navDispatch, intl.formatMessage({ id: 'capitalHarness.downloadError' }))
                              }
                            }}
                          >
                            {intl.formatMessage({ id: 'forms.download' })}
                          </Button>
                        ) : (
                          <Typography.Text className="status-msg not-generated">
                            {intl.formatMessage({ id: 'capitalHarness.fileNotGenerated' })}
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                  )
                })}
                <div className="file-item">
                  <div className="files-cell file-name">
                    <Flex align="center" gap={8}>
                      <IconXmlFile />
                      <Typography.Text>{record.name}.xml</Typography.Text>
                    </Flex>
                  </div>
                  <div className="files-cell file-status">
                    {record?.capital_harness_file && record.id in sourceDownloads ? (
                      sourceDownloads[record.id] === null ? (
                        <ProgresSpinner
                          caption={intl.formatMessage({ id: 'capitalHarness.downloadingSource' })}
                          className="upload-inprogress"
                        />
                      ) : (
                        <Flex vertical gap={2} data-testid="capital-harness-source-progress">
                          <Typography.Text type="secondary">
                            {intl.formatMessage({ id: 'capitalHarness.downloadingSource' })}
                          </Typography.Text>
                          <Progress percent={sourceDownloads[record.id]} size="small" status="active" />
                        </Flex>
                      )
                    ) : record?.capital_harness_file ? (
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        className="link-button"
                        data-testid="capital-harness-source-download"
                        onClick={() => downloadSourceFile(record)}
                      >
                        {intl.formatMessage({ id: 'capitalHarness.downloadSourceFile' })}
                      </Button>
                    ) : (
                      <Typography.Text className="status-msg not-generated">
                        {intl.formatMessage({ id: 'capitalHarness.fileNotGenerated' })}
                      </Typography.Text>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        },
        rowExpandable: () => true,
      }}
      pagination={
        pagination
          ? {
              current: pagination?.currentPage,
              total: pagination?.totalCount,
              pageSize: 10,
              showSizeChanger: false,
              hideOnSinglePage: true,
              showTotal: (total) => <RecordsCount count={total} />,
              onChange: (page) => {
                const offset = (page - 1) * 10
                const url = pagination?.nextPageLink
                  ? new URL(pagination?.nextPageLink)
                  : new URL(pagination?.prevPageLink)
                url.searchParams.set('offset', offset)
                props.getCapitalHarnessList(url.toString(), true)
              },
            }
          : false
      }
      locale={{
        emptyText: (
          <div style={{ padding: '40px' }}>
            <Empty description={<RecordsCount count={0} />} />
          </div>
        ),
      }}
    />
  )
}

export default CapitalHarnessTable
